import axiosApiClient from "..";
import { handleError } from "../../utils/errors";

/**
 * Games API service. The server is the source of truth for credits, outcomes,
 * payouts, round state, and fairness data.
 */
const BASE_URL = "/games";
let fallbackIdempotencyCounter = 0;

const makeIdempotencyKey = (provided) => {
  if (provided) return String(provided);
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  if (typeof window !== "undefined" && window.crypto?.getRandomValues) {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  }
  fallbackIdempotencyCounter += 1;
  return `${Date.now()}-${fallbackIdempotencyCounter}`;
};

const postGame = (path, body, idempotencyKey) => {
  const key = makeIdempotencyKey(idempotencyKey);
  return axiosApiClient.post(
    path,
    { ...body, idempotency_key: key },
    { headers: { "X-Idempotency-Key": key } },
  );
};

class GamesService {
  // POST /api/games/coinflip/play  { bet, side } -> CoinFlipResponse
  // { result_side, won, bet, payout, net, new_credits, nonce, downgraded }
  playCoinFlip = async ({ bet, side, idempotencyKey }) => {
    try {
      const response = await postGame(`${BASE_URL}/coinflip/play`, {
        bet,
        side,
      }, idempotencyKey);
      if (response.status !== 200) throw new Error();
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while flipping the coin.");
    }
  };

  // POST /api/games/dice/play  { bet, threshold, direction } -> DiceResponse
  // { roll, won, multiplier, bet, payout, net, new_credits, nonce, downgraded }
  playDice = async ({ bet, threshold, direction, idempotencyKey }) => {
    try {
      const response = await postGame(`${BASE_URL}/dice/play`, {
        bet,
        threshold,
        direction,
      }, idempotencyKey);
      if (response.status !== 200) throw new Error();
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while rolling the dice.");
    }
  };

  // POST /api/games/lucky-dice/play  { bet } -> LuckyDiceResponse
  // { face, multiplier, jackpot, streak_after, bet, payout, net, new_credits, nonce, downgraded }
  playRollDice = async ({ bet, idempotencyKey }) => {
    try {
      const response = await postGame(`${BASE_URL}/lucky-dice/play`, { bet }, idempotencyKey);
      if (response.status !== 200) throw new Error();
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while throwing the dice.");
    }
  };

  // POST /api/games/scratch/play  { bet } -> ScratchResponse
  // { multiplier, bet, payout, net, new_credits, nonce, downgraded }
  playScratch = async ({ bet, idempotencyKey }) => {
    try {
      const response = await postGame(`${BASE_URL}/scratch/play`, {
        bet,
      }, idempotencyKey);
      if (response.status !== 200) throw new Error();
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while scratching the card.");
    }
  };

  // POST /api/games/spin/play  { bet } -> SpinResponse
  // { segment_index, multiplier, bet, payout, net, new_credits, nonce, downgraded }
  playSpin = async ({ bet, idempotencyKey }) => {
    try {
      const response = await postGame(`${BASE_URL}/spin/play`, {
        bet,
      }, idempotencyKey);
      if (response.status !== 200) throw new Error();
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while spinning.");
    }
  };

  // POST /api/games/drop/play  { bet, ball_count } -> DropResponse
  // { balls:[{path,bucket_index,multiplier,payout,downgraded}], bet, ball_count,
  //   total_payout, total_wagered, net, new_credits, nonce, downgraded }
  playDrop = async ({ bet, ballCount, idempotencyKey }) => {
    try {
      const response = await postGame(`${BASE_URL}/drop/play`, {
        bet,
        ball_count: ballCount,
      }, idempotencyKey);
      if (response.status !== 200) throw new Error();
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while dropping.");
    }
  };

  // ─── Mines (multi-step: start -> reveal -> cashout) ───────────────
  getActiveMines = async () => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/mines/active`);
      if (response.status !== 200) throw new Error();
      return response.data; // { round: MinesActiveRound | null }
    } catch (err) {
      throw handleError(err, "Error occurred while loading mines round.");
    }
  };

  startMines = async ({ bet, waspCount, idempotencyKey }) => {
    try {
      const response = await postGame(`${BASE_URL}/mines/start`, {
        bet,
        wasp_count: waspCount,
      }, idempotencyKey);
      if (response.status !== 200) throw new Error();
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while starting mines.");
    }
  };

  revealMines = async ({ roundId, cellIndex, idempotencyKey }) => {
    try {
      const response = await postGame(`${BASE_URL}/mines/reveal`, {
        round_id: roundId,
        cell_index: cellIndex,
      }, idempotencyKey);
      if (response.status !== 200) throw new Error();
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while revealing a cell.");
    }
  };

  cashoutMines = async ({ roundId, idempotencyKey }) => {
    try {
      const response = await postGame(`${BASE_URL}/mines/cashout`, {
        round_id: roundId,
      }, idempotencyKey);
      if (response.status !== 200) throw new Error();
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while cashing out.");
    }
  };

  // ─── Slots (paylines + scatter free-spins + double-up gamble) ─────
  // POST /api/games/slots/play  { bet } -> SlotsResponse
  // { grid, lines, bet, payout, base_payout, net, free_spins, new_credits, nonce, downgraded }
  playSlots = async ({ bet, idempotencyKey }) => {
    try {
      const response = await postGame(`${BASE_URL}/slots/play`, {
        bet,
      }, idempotencyKey);
      if (response.status !== 200) throw new Error();
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while spinning the slots.");
    }
  };

  // POST /api/games/slots/buy-bonus  { bet } -> SlotsResponse (+ price)
  buySlotsBonus = async ({ bet, idempotencyKey }) => {
    try {
      const response = await postGame(`${BASE_URL}/slots/buy-bonus`, {
        bet,
      }, idempotencyKey);
      if (response.status !== 200) throw new Error();
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while buying the bonus.");
    }
  };

  // POST /api/games/slots/gamble  { nonce, pick } -> SlotsGambleResponse
  // { won, result_color, amount, net, new_credits, step, winnings, can_continue }
  playSlotsGamble = async ({ nonce, pick, idempotencyKey }) => {
    try {
      const response = await postGame(`${BASE_URL}/slots/gamble`, {
        nonce,
        pick,
      }, idempotencyKey);
      if (response.status !== 200) throw new Error();
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while gambling.");
    }
  };

  // ─── Bunny Crash (multi-step: start -> step -> cashout) ──────────
  getActiveBunnyCrash = async () => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/bunny-crash/active`);
      if (response.status !== 200) throw new Error();
      return response.data; // { round: BunnyActiveRound | null }
    } catch (err) {
      throw handleError(err, "Error occurred while loading the round.");
    }
  };

  startBunnyCrash = async ({ bet, difficulty, idempotencyKey }) => {
    try {
      const response = await postGame(`${BASE_URL}/bunny-crash/start`, { bet, difficulty }, idempotencyKey);
      if (response.status !== 200) throw new Error();
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while starting the round.");
    }
  };

  stepBunnyCrash = async ({ roundId, expectedSteps, idempotencyKey }) => {
    try {
      const response = await postGame(
        `${BASE_URL}/bunny-crash/step`,
        { round_id: roundId, expected_steps: expectedSteps },
        idempotencyKey,
      );
      if (response.status !== 200) throw new Error();
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while hopping.");
    }
  };

  cashoutBunnyCrash = async ({ roundId, idempotencyKey }) => {
    try {
      const response = await postGame(`${BASE_URL}/bunny-crash/cashout`, { round_id: roundId }, idempotencyKey);
      if (response.status !== 200) throw new Error();
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while cashing out.");
    }
  };

  // GET /api/games/:slug/recent -> { rounds: [...] }
  // Used by the "View all" history modal. Optional — fails soft.
  getRecentRounds = async ({ slug }) => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/${slug}/recent`);
      if (response.status !== 200) throw new Error();
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching recent rounds.");
    }
  };

  // GET /api/games/live-wins?limit=100 -> { wins: [...] }
  // Latest positive-net rounds across all games. Used by the games hub ticker.
  getLiveWins = async ({ limit = 100 } = {}) => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/live-wins`, {
        params: { limit },
      });
      if (response.status !== 200) throw new Error();
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching live wins.");
    }
  };
}

const gamesService = new GamesService();

export default gamesService;
